import { TimelineSection } from '../../_models/timeline-models/section-listing.model';
import { StudentTimelineDateFrame, StudentTimelineFrame } from '../../_models/timeline-models/student-timeline-frame.model';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnInit, Output, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';

import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PlacementService } from '../../_services/index';
import { TimelineSectionsService } from '../../_services/timeline-sections/timeline-sections.service';
import { TutorReportsHelperService } from '../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { TimelineNavComponent } from './timeline-nav/timeline-nav.component';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { TimelineSectionStudentInfo } from '../../_models/timeline-models/timeline-section-student-info.model';
import { SelectTimelineSubjectModalComponent } from '../select-timeline-subject/select-timeline-subject-modal/select-timeline-subject-modal.component';
import { TimelineHelper } from '../../_helpers/learning-plan-helper/timeline.helper';
import { MilestoneService } from '../../_services/milestone/milestone.service';
import { MilestoneEvent } from '../../_models/milestone/milestone-event.model';
import { MilestoneEventSection } from '../../_models/timeline-models/section-models/milestone-event-section.model';
import { TimelineSectionStatus } from '../../_models/timeline-models/timeline-section-status.enum';
import { isClientUser } from '../../_helpers/can-use/current-user-roles';
import { clientVisibleSections } from './client-visible-sections';

@Component({
    selector: 'tutor-report-timeline',
    templateUrl: './tutor-report-timeline.component.html',
    styleUrls: [
        './tutor-report-timeline.component.css',
        './tutor-report-timeline-learning-section-component.css',
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class TutorReportTimelineComponent implements OnInit, AfterViewInit {

  @ViewChild("timeline", { static: true }) timeline: ElementRef;
  @ViewChild("nav", { static: true }) nav: TimelineNavComponent;
  @ViewChildren('sections') sections: QueryList<ElementRef>;

  public timelineList: TimelineSection[];
  public timelineDateFrame: StudentTimelineDateFrame[];
  public scrollTop: number;
  public parallax: boolean = true;
  public placementId: number;
  public timelineStudent: TimelineSectionStudentInfo = new TimelineSectionStudentInfo(0, "");
  public subject: string = "";
  public clientId: number;
  public todaysDate: Date;

  constructor(
    private renderer: Renderer2,
    private dialog: MatDialog,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
    private tutorReportsHelperService: TutorReportsHelperService,
    private timelineSectionService: TimelineSectionsService,
    private placementService: PlacementService,
    private milestoneService: MilestoneService,
    private errorModalHelper: ErrorModalHelperService
  ) {
    // Placement id comes from the route (/timeline/:id). The finer state (student/subject/client)
    // is still passed via nav state and cannot be reconstructed from the id alone (see plan);
    // a cold external link therefore still requires that state.
    this.placementId = Number(this.route.snapshot.paramMap.get('id'));
    this.tutorReportsHelperService.ensureContext(this.placementId);

    const nav = this.router.getCurrentNavigation();
    if (!isNaN(this.placementId) && this.placementId > 0
      && nav?.extras?.state?.timelineStudent != null
      && nav?.extras?.state?.subject != null
      && nav?.extras?.state?.clientId != null) {

      const insertedState = nav.extras.state;

      this.timelineStudent = insertedState.timelineStudent as TimelineSectionStudentInfo;
      this.subject = insertedState.subject as string;
      this.clientId = insertedState.clientId as number;

      this.loadTimeline();
    }
    else {
      this.router.navigate(["/"]);
    }

  }

  ngOnInit() {
  }

  ngAfterViewInit() {

    this.sections.changes.subscribe((s) => {
      setTimeout(() => {
        if (this.timelineList != null && this.timelineList.length > 0) {
          document.getElementById('last').scrollIntoView({ behavior: "auto" }); // Smooth scroll may be conflicting with Nav listener.
        }
      }, 1000);
    });


  }



  @HostListener("window:scroll")
  scrollFn() {
    this.scrollTop = window.pageYOffset;
    if (this.parallax == true) {
      this.renderer.setStyle(this.timeline.nativeElement, 'background-position-y', Math.floor(window.pageYOffset / 1.8) + 'px');
    }
  }

  private loadTimeline(): void {
    // The baseline frame carries the keyStages/targetGrades reference lists that the classic
    // sections use to resolve their key-stage / learning-band ids to text. It must be applied
    // before timelineList is assigned, because each section resolves those ids synchronously in
    // ngOnInit as the @for renders it.
    forkJoin({
      baselineFrame: this.placementService.GetBaselineFrame(this.placementId, this.timelineStudent.studentId),
      timelineFrame: this.timelineSectionService.getTimelineSections(this.placementId, this.timelineStudent.studentId, this.subject, this.clientId),
      milestones: this.milestoneService.list(this.placementId, this.timelineStudent.studentId)
    }).subscribe(({ baselineFrame, timelineFrame, milestones }: { baselineFrame: any, timelineFrame: StudentTimelineFrame, milestones: MilestoneEvent[] }) => {
      this.tutorReportsHelperService.setBaselineFrame(baselineFrame);
      // Milestones come from a separate endpoint; merge them into the server-ordered sections and
      // re-sort by date so they slot in chronologically alongside sessions / ILP / progress reports.
      const milestoneSections = (milestones || []).map(m => this.toMilestoneSection(m));
      // A client user sees an ILP or progress report only once it is approved.
      const sections = isClientUser() ? clientVisibleSections(timelineFrame.sections) : timelineFrame.sections;
      this.timelineList = [...sections, ...milestoneSections]
        .sort((a, b) => new Date(a.sectionDate).getTime() - new Date(b.sectionDate).getTime());
      this.timelineDateFrame = timelineFrame.timeFrame;
    }, error => {
      console.error(error);
      this.errorModalHelper.show(error.message, error);
    });
  }


  private toMilestoneSection(milestone: MilestoneEvent): MilestoneEventSection {
    const section = new MilestoneEventSection();
    section.sectionType = 'milestone-event';
    section.sectionDate = milestone.eventDate;
    section.id = milestone.id;
    section.status = TimelineSectionStatus.Saved;
    section.placementId = milestone.placementId;
    section.subject = this.subject;
    section.eventDescription = milestone.eventDescription;
    section.createdByName = milestone.createdByName;
    return section;
  }

  public backClick() {
    this.location.back();
  }

  public openSubjectModal() {
    const dialogRef = this.dialog.open(SelectTimelineSubjectModalComponent, {
      data: {
        studentId: this.timelineStudent.studentId,
        studentName: this.timelineStudent.studentFullName,
        placementId: this.placementId,
        clientId: this.clientId
      },
      panelClass: 'my-timeline-edit-dialog'
    });
    dialogRef.afterClosed().subscribe(() => {

    });
  }

  public refreshTimeline(reload: boolean): void {
    if (reload) {
      this.loadTimeline();
    }
  }
}
